/**
 * iter74 — Abandoned-cart tracker.
 *
 * When the customer is logged in AND has at least one Ready-Made item in
 * their cart, POST /api/cart/track-abandon so the backend knows to email
 * a recovery link 30 min later.
 *
 * Behavior:
 *  - Throttles to at most 1 request per 60s per session (localStorage-backed).
 *  - Runs on any cart change (debounced 2s) so we always have the latest snapshot.
 *  - Silent — no toasts, no error UI. Recovery is a best-effort background feature.
 *  - Clears the tracked cart when items go to zero.
 */
import { useEffect, useRef } from "react";
import { useCart } from "../lib/cart";
import { useAuth } from "../lib/auth";
import { apiClient } from "../lib/api";

const THROTTLE_MS = 60_000;
const DEBOUNCE_MS = 2_000;
const KEY = "tc_cart_track_last";

function _canTrack() {
  try {
    const last = Number(localStorage.getItem(KEY) || 0);
    return Date.now() - last > THROTTLE_MS;
  } catch { return true; }
}

function _stamp() {
  try { localStorage.setItem(KEY, String(Date.now())); } catch {}
}

async function _postSnapshot(items) {
  // Only send fields the backend needs to re-enrich each line.
  const stripped = items.map((it) => ({
    type: it.type,
    product_id: it.product_id,
    garment_type: it.garment_type,
    size: it.size,
    selected_color: it.selected_color,
    quantity: it.quantity,
    // Custom-product fields (backend enrich_line reads these too)
    style: it.style,
    color: it.color,
    layers: it.layers,
    print_sides: it.print_sides,
  }));
  try {
    // apiClient's interceptor auto-attaches the Bearer token from
    // localStorage — no need to pass it explicitly here.
    await apiClient.post("/cart/track-abandon", { items: stripped }, { timeout: 6000 });
    _stamp();
  } catch { /* silent */ }
}

export default function AbandonedCartTracker() {
  const { items } = useCart();
  const { user } = useAuth();
  const debounceRef = useRef(null);

  useEffect(() => {
    // Only track logged-in customers — anonymous carts have no email.
    if (!user?.email) return;

    // Debounce so rapid additions/removals don't spam the backend.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (!_canTrack()) return;
      _postSnapshot(items);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [items, user?.email]);

  return null;                             // headless — no UI
}
